import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Disc, Plus, X, Loader2, RadioTower, Download, Construction, CopyX, Wand2, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { downloadBlob } from "@/components/riptag/util";
import { ArtThumb } from "@/components/riptag/TrackTable";
import { DecadeAutoModal } from "@/components/musiclib/DecadeAutoModal";
import { GenreSuggestModal } from "@/components/musiclib/GenreSuggestModal";

const DECADE_COLORS = {
  "1960s": "from-rose-500/20", "1970s": "from-amber-500/20", "1980s": "from-fuchsia-500/20",
  "1990s": "from-sky-500/20", "2000s": "from-emerald-500/20", "2010s": "from-indigo-500/20",
  "2020s": "from-cyan-500/20", Unclassified: "from-slate-500/20",
};

export const DecadesPanel = ({ onPick }) => {
  const [decades, setDecades] = useState(null);
  const [autoOpen, setAutoOpen] = useState(false);
  const load = () => api.get("/music/filters").then((r) => setDecades(r.data.decades || [])).catch(() => setDecades([]));
  useEffect(() => { load(); }, []);
  if (decades === null) return <Loader2 size={16} className="animate-spin text-ink-muted" />;
  return (
    <div data-testid="ml-decades">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <p className="type-body text-ink-muted flex-1 min-w-56">
          Decades come from <b>original recording years</b> — not compilation reissue dates. Tracks without a usable year land in <b>Unclassified</b>.
        </p>
        <button onClick={() => setAutoOpen(true)} data-testid="ml-decade-auto-btn"
          className="type-button-label flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-2 text-bg-base hover:opacity-90">
          <Wand2 size={13} /> Auto-assign decades
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {decades.map((d) => (
          <button key={d.decade} onClick={() => onPick(d.decade)} data-testid={`ml-decade-${d.decade}`}
            className={`rounded-xl border border-line bg-gradient-to-br ${DECADE_COLORS[d.decade] || "from-slate-500/20"} to-transparent bg-bg-surface p-4 text-left hover:bg-white/5 transition`}>
            <Disc size={16} className="text-ink-muted mb-2" />
            <div className="text-lg font-bold text-ink-primary font-display">{d.decade}</div>
            <div className="type-list-subtitle text-ink-muted">{d.n.toLocaleString()} track{d.n === 1 ? "" : "s"}</div>
          </button>
        ))}
      </div>
      {autoOpen && (
        <DecadeAutoModal onClose={() => setAutoOpen(false)}
          onDone={() => { setAutoOpen(false); load(); }} />
      )}
    </div>
  );
};

export const GenresPanel = ({ onPick }) => {
  const [genres, setGenres] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const load = () => api.get("/music/genres").then((r) => setGenres(r.data.genres || [])).catch(() => setGenres([]));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/music/genres", { name }); toast.success(`Genre category "${name.trim()}" created`); setName(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add genre"); }
    setBusy(false);
  };
  const remove = async (g) => {
    try { await api.delete(`/music/genres/${encodeURIComponent(g)}`); toast.success("Custom genre removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
  };

  if (genres === null) return <Loader2 size={16} className="animate-spin text-ink-muted" />;
  return (
    <div data-testid="ml-genres">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="type-body text-ink-muted flex-1 min-w-56">
          Tracks can hold multiple genres (set them in Track Details). Click a category to browse it; add your own below.
          Suggestions use file tags and MusicBrainz first — AI only when both are empty, with your approval.
        </p>
        <button onClick={() => setSuggestOpen(true)} data-testid="ml-genre-suggest-btn"
          className="type-button-label flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-2 text-bg-base hover:opacity-90">
          <Sparkles size={13} /> AI genre suggestions
        </button>
      </div>
      <div className="flex gap-2 mb-4 max-w-sm">
        <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="New genre category…" data-testid="ml-genre-add-input"
          className="flex-1 rounded-md border border-line bg-bg-surface px-3 py-2 text-xs focus:border-brand-amber focus:outline-none" />
        <button onClick={add} disabled={busy || !name.trim()} data-testid="ml-genre-add-btn"
          className="type-button-label flex items-center gap-1 rounded-md bg-brand-amber px-3 py-2 text-bg-base disabled:opacity-50">
          <Plus size={13} /> Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {genres.map((g) => (
          <span key={g.name} className="inline-flex items-center gap-1.5 rounded-full border border-line bg-bg-surface px-3 py-1.5 hover:border-amber-500/50 transition"
            data-testid={`ml-genre-${g.name}`}>
            <button onClick={() => onPick(g.name)} className="text-xs text-ink-primary">
              {g.name} <span className="text-ink-muted">({g.count})</span>
            </button>
            {g.custom && (
              <button onClick={() => remove(g.name)} className="text-ink-muted hover:text-red-400" data-testid={`ml-genre-del-${g.name}`}>
                <X size={11} />
              </button>
            )}
          </span>
        ))}
      </div>
      {suggestOpen && <GenreSuggestModal onClose={() => setSuggestOpen(false)} onDone={load} />}
    </div>
  );
};

export const DuplicatesPanel = () => {
  const [groups, setGroups] = useState(null);
  useEffect(() => { api.get("/music/duplicates").then((r) => setGroups(r.data.groups || [])).catch(() => setGroups([])); }, []);
  if (groups === null) return <Loader2 size={16} className="animate-spin text-ink-muted" />;
  return (
    <div data-testid="ml-duplicates">
      {groups.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line p-8 text-center type-body text-ink-muted" data-testid="ml-dup-empty">
          <CopyX size={22} className="mx-auto mb-2 text-emerald-400" />
          No duplicate candidates — every file hash in your library is unique.
        </div>
      ) : groups.map((g, i) => (
        <div key={i} className="rounded-xl border border-line bg-bg-surface p-4 mb-3">
          <div className="type-status-badge font-mono uppercase tracking-wider text-rose-300 mb-2">// {g.reason}</div>
          <div className="space-y-1.5">
            {g.tracks.map((t) => (
              <div key={t.track_id} className="flex items-center gap-3">
                <ArtThumb url={t.art_url} size="w-8 h-8" />
                <div className="min-w-0 flex-1">
                  <div className="type-list-title text-ink-primary truncate">{t.title || t.filename}</div>
                  <div className="type-list-subtitle text-ink-muted truncate">{t.artist || "Unknown"} · {t.album || "—"} · {Math.round(t.duration || 0)}s</div>
                </div>
                <span className="type-timestamp text-ink-muted shrink-0">{((t.size || 0) / 1024 / 1024).toFixed(1)} MB</span>
              </div>
            ))}
          </div>
          <p className="type-timestamp text-ink-muted mt-2">Review in the Music Library — select and set status "inactive", or delete from the Rip &amp; Tag library view.</p>
        </div>
      ))}
    </div>
  );
};

export const StationsPanel = ({ onPick }) => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/music/stations").then((r) => setData(r.data)).catch(() => setData({ stations: [] })); }, []);
  if (data === null) return <Loader2 size={16} className="animate-spin text-ink-muted" />;
  return (
    <div data-testid="ml-stations">
      <p className="type-body text-ink-muted mb-4">
        One library, many stations — assign a track to several stations without copying the audio file.
        Customers only ever see their own account's libraries.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {data.stations.map((s) => (
          <button key={s.station_id} onClick={() => onPick(s.station_id)} data-testid={`ml-station-${s.station_id}`}
            className={`rounded-xl border p-4 text-left hover:bg-white/5 transition ${
              s.station_id === data.active ? "border-amber-500/50 bg-amber-500/5" : "border-line bg-bg-surface"}`}>
            <RadioTower size={15} className="text-brand-amber mb-2" />
            <div className="type-list-title text-ink-primary truncate">{s.name}</div>
            <div className="type-list-subtitle text-ink-muted">{s.tracks.toLocaleString()} assigned track{s.tracks === 1 ? "" : "s"}</div>
            {s.station_id === data.active && <span className="type-timestamp text-brand-amber">Active workspace</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

export const ExportPanel = () => {
  const [busy, setBusy] = useState("");
  const dl = async (kind) => {
    setBusy(kind);
    try {
      const r = await api.get(`/rip/export/${kind}`, { params: { scope: "library" }, responseType: "blob" });
      downloadBlob(r.data, kind === "csv" ? "broadcastflow_library.csv" : "broadcastflow_library.m3u8");
      toast.success(kind === "csv" ? "Library CSV downloaded" : "M3U8 playlist downloaded");
    } catch { toast.error("Export failed"); }
    setBusy("");
  };
  return (
    <div data-testid="ml-export">
      <p className="type-body text-ink-muted mb-4">
        File exports work today — drop them straight into SAM Broadcaster PRO, Myriad or RadioDJ. Live playout sync arrives in a later phase.
      </p>
      <div className="flex flex-wrap gap-2 mb-5">
        <button onClick={() => dl("m3u8")} disabled={!!busy} data-testid="ml-export-m3u8"
          className="type-button-label flex items-center gap-1.5 rounded-md bg-brand-amber px-3 py-2 text-bg-base disabled:opacity-50">
          {busy === "m3u8" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Export M3U8 playlist
        </button>
        <button onClick={() => dl("csv")} disabled={!!busy} data-testid="ml-export-csv"
          className="type-button-label flex items-center gap-1.5 rounded-md border border-line px-3 py-2 text-ink-primary hover:bg-white/5 disabled:opacity-50">
          {busy === "csv" ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Export CSV (SAM-compatible)
        </button>
      </div>
      <DevStatus title="Live playout sync" phase="Later phase" inline
        description="Direct push into SAM Broadcaster PRO, Myriad Playout and RadioDJ libraries, plus the Radio Rotation Manager."
        bullets={["SAM Broadcaster PRO live sync", "Myriad Playout integration", "RadioDJ auto-import", "Rotation Manager handoff"]} />
    </div>
  );
};

export const DevStatus = ({ title, phase, description, bullets = [], inline = false }) => (
  <div className={`rounded-xl border border-dashed border-line ${inline ? "p-4" : "p-8"} ${inline ? "" : "max-w-2xl"}`}
    data-testid={`ml-dev-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="flex items-center gap-2 mb-2">
      <Construction size={15} className="text-brand-amber" />
      <span className="type-section-heading text-ink-primary">{title}</span>
      <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-mono uppercase text-brand-amber">
        In development · {phase}
      </span>
    </div>
    <p className="type-body text-ink-muted mb-3">{description}</p>
    <ul className="space-y-1">
      {bullets.map((b) => <li key={b} className="type-list-subtitle text-ink-secondary font-mono">· {b}</li>)}
    </ul>
  </div>
);
